import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Modal,
  CircularProgress,
  Button,
  TextField,
  Popover,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close,
  ShowChart,
  CalendarMonthOutlined,
  Download,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import axios from 'axios';
import { btnGradient, fontStack } from './auth/authStyles';

const C = {
  bg: '#1a1b2e',
  card: '#2E2F45',
  input: '#25263B',
  border: '#3A3B55',
  textPrimary: '#FFFFFF',
  textMuted: '#A7AEC2',
  textPlaceholder: '#6D7593',
  accentBlue: '#22D3EE',
};

const TYPE_META = {
  climatrack: { color: '#34D399', tint: 'rgba(52,211,153,0.13)', border: 'rgba(5,150,105,0.55)' },
  aquacheck: { color: '#22D3EE', tint: 'rgba(34,211,238,0.13)', border: 'rgba(34,211,238,0.55)' },
  greenwatch: { color: '#F59E0B', tint: 'rgba(245,158,11,0.13)', border: 'rgba(245,158,11,0.55)' },
};

const PERIODS = [
  { id: 'yesterday', label: 'Hier' },
  { id: '30min', label: '30 min', ms: 30 * 60 * 1000 },
  { id: '1h', label: '1 h', ms: 60 * 60 * 1000 },
  { id: '24h', label: '24 h', ms: 24 * 60 * 60 * 1000 },
  { id: '48h', label: '48 h', ms: 48 * 60 * 60 * 1000 },
];

const TABLE_SERIES = {
  climatrack: [
    { id: 'temperature', field: 'temperature', label: 'Temperature', unit: '°C', color: '#FB7185' },
    { id: 'humidity', field: 'humidity', label: 'Humidité', unit: '%', color: '#38BDF8' },
    { id: 'co2', field: 'co2', label: 'CO2', unit: 'ppm', color: '#FBBF24' },
    { id: 'tvoc', field: 'tvoc', label: 'TVOC', unit: 'ppb', color: '#34D399' },
    { id: 'sound', field: 'sound_level', label: 'Son', unit: 'dB', color: '#A78BFA' },
    { id: 'pm1_0', field: 'pm1_0', label: 'PM1.0', unit: 'µg/m³', color: '#94A3B8' },
    { id: 'pm2_5', field: 'pm2_5', label: 'PM2.5', unit: 'µg/m³', color: '#F472B6' },
    { id: 'pm10', field: 'pm10', label: 'PM10', unit: 'µg/m³', color: '#60A5FA' },
  ],
  aquacheck: [
    { id: 'temperature', field: 'temperature', label: 'Temperature', unit: '°C', color: '#FB7185' },
    { id: 'humidity', field: 'humidity', label: 'Humidité', unit: '%', color: '#38BDF8' },
    { id: 'ground_humidity', field: 'ground_humidity', label: 'Humidité sol', unit: '%', color: '#34D399' },
  ],
  greenwatch: [
    { id: 'temperature', field: 'temperature', label: 'Temperature', unit: '°C', color: '#FB7185' },
    { id: 'humidity', field: 'humidity', label: 'Humidité', unit: '%', color: '#38BDF8' },
    { id: 'co2', field: 'co2', label: 'CO2', unit: 'ppm', color: '#FBBF24' },
  ],
};

const getSeriesForTable = (table) => TABLE_SERIES[table] || TABLE_SERIES.climatrack;

const MOBILE_PAD = '22px';

const getTrendMeta = (data, seriesId) => {
  if (data.length < 2) return null;
  const first = data[0][1];
  const last = data[data.length - 1][1];
  const diff = last - first;
  const pct = first !== 0 ? (diff / Math.abs(first)) * 100 : 0;
  const up = diff >= 0;
  const alertRising = ['co2', 'sound', 'pm2_5', 'pm10', 'pm1_0', 'tvoc'].includes(seriesId);
  return {
    up,
    label: `${up ? '+' : ''}${pct.toFixed(1)}%`,
    color: alertRising && up ? '#FF5722' : undefined,
  };
};

const toDateTimeLocalValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (value) => String(value).padStart(2, '0');

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const fromDateTimeLocalValue = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const baseChartOptions = {
  chart: {
    backgroundColor: 'transparent',
    spacing: [8, 8, 8, 8],
    zoomType: 'x',
  },
  title: { text: null },
  credits: { enabled: false },
  legend: { enabled: false },
  time: { useUTC: false },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 1,
    gridLineColor: 'rgba(255,255,255,0.06)',
    lineColor: 'rgba(255,255,255,0.12)',
    tickColor: 'rgba(255,255,255,0.12)',
    labels: { style: { color: C.textPlaceholder, fontSize: '10px' } },
  },
  yAxis: {
    title: { text: null },
    gridLineColor: 'rgba(255,255,255,0.06)',
    labels: { style: { color: C.textPlaceholder, fontSize: '10px' } },
  },
  tooltip: {
    shared: true,
    backgroundColor: C.card,
    borderColor: C.border,
    style: { color: C.textPrimary },
  },
};

const DeviceCurvesModal = ({ open, onClose, device }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const [period, setPeriod] = useState('24h');
  const [viewMode, setViewMode] = useState('combined');
  const [visible, setVisible] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [calendarAnchor, setCalendarAnchor] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);

  const seriesDefs = useMemo(
    () => (device ? getSeriesForTable(device.table) : []),
    [device]
  );

  useEffect(() => {
    if (!device || !open) return;
    const init = {};
    getSeriesForTable(device.table).forEach((s) => { init[s.id] = true; });
    setVisible(init);
    setPeriod('24h');
    setViewMode('combined');
    setCustomStart(null);
    setCustomEnd(null);
    setError('');
  }, [device, open]);

  const getRange = useCallback(() => {
    if (
      customStart instanceof Date &&
      customEnd instanceof Date &&
      !Number.isNaN(customStart.getTime()) &&
      !Number.isNaN(customEnd.getTime()) &&
      customStart < customEnd
    ) {
      return {
        start: customStart,
        end: customEnd,
      };
    }

    const now = new Date();

    /*
     * Bouton "Hier" :
     * affiche uniquement les données de la journée précédente,
     * de 00:00 hier à 00:00 aujourd'hui.
     */
    if (period === 'yesterday') {
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        0,
        0,
        0,
        0
      );

      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        0
      );

      return {
        start,
        end,
      };
    }

    const preset =
      PERIODS.find(
        (p) =>
          p.id === period &&
          typeof p.ms === 'number'
      ) ||
      PERIODS.find((p) => p.id === '24h');

    const end = new Date();

    const start = new Date(
      end.getTime() - preset.ms
    );

    return {
      start,
      end,
    };
  }, [period, customStart, customEnd]);

  const selectedRange = useMemo(() => {
    return getRange();
  }, [getRange]);
  const dayChangePlotLines = useMemo(() => {
    const lines = [];

    if (!selectedRange?.start || !selectedRange?.end) {
      return lines;
    }

    const start = new Date(selectedRange.start);
    const end = new Date(selectedRange.end);

    const cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);

    if (cursor.getTime() <= start.getTime()) {
      cursor.setDate(cursor.getDate() + 1);
    }

    while (cursor.getTime() < end.getTime()) {
      const labelDate = cursor.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
      });

      lines.push({
        value: cursor.getTime(),
        color: 'rgba(250, 204, 21, 0.28)',
        width: 1,
        zIndex: 5,
        dashStyle: 'LongDash',
        label: {
          text: labelDate,
          rotation: 0,
          y: 16,
          x: 4,
          style: {
            color: 'rgba(250, 204, 21, 0.75)',
            fontWeight: '700',
            fontSize: '11px',
          },
        },
      });

      cursor.setDate(cursor.getDate() + 1);
    }

    return lines;
  }, [selectedRange]);

  const rangeXAxisOptions = useMemo(() => ({
    ...baseChartOptions.xAxis,
    min: selectedRange.start.getTime(),
    max: selectedRange.end.getTime(),
    startOnTick: false,
    endOnTick: false,
    plotLines: dayChangePlotLines,
    labels: {
      ...baseChartOptions.xAxis.labels,
      useHTML: true,
      formatter: function () {
        const date = new Date(this.value);

        const isMidnight =
          date.getHours() === 0 &&
          date.getMinutes() === 0;

        if (isMidnight) {
          return `<span style="color:rgba(250, 204, 21, 0.75);font-weight:700;">${Highcharts.dateFormat('%d/%m', this.value)}</span>`;
        }

        return `<span style="color:#6D7593;">${Highcharts.dateFormat('%H:%M', this.value)}</span>`;
      },
    },
  }), [
    selectedRange,
    dayChangePlotLines,
  ]);

  useEffect(() => {
    if (!open || !device) return undefined;

    const { start, end } = selectedRange;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('token');

        const response = await axios.get(
          `${API_URL}/api/data/${device.table}/${device.id}/filter`,
          {
            params: {
              startTime: start.toISOString(),
              endTime: end.toISOString(),
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!cancelled) {
          setRows(Array.isArray(response.data) ? response.data : []);
          setFetchedAt(new Date());
        }
      } catch (err) {
        if (!cancelled) {
          setError('Impossible de charger les données pour cette période.');
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    device,
    selectedRange,
    API_URL,
  ]);

  const seriesData = useMemo(() => {
    const map = {};
    seriesDefs.forEach((def) => {
      map[def.id] = rows
        .filter((row) => row[def.field] !== null && row[def.field] !== undefined && row[def.field] !== '')
        .map((row) => [new Date(row.timestamp).getTime(), parseFloat(row[def.field])])
        .filter(([, v]) => !Number.isNaN(v));
    });
    return map;
  }, [rows, seriesDefs]);

  const activeSeries = useMemo(
    () => seriesDefs.filter((s) => visible[s.id] !== false),
    [seriesDefs, visible]
  );

  const periodLabel = useMemo(() => {
    if (customStart && customEnd) return 'dates précises';
    return PERIODS.find((p) => p.id === period)?.label || '24 h';
  }, [period, customStart, customEnd]);

  const combinedOptions = useMemo(() => ({
    ...baseChartOptions,

    chart: {
      ...baseChartOptions.chart,
      height: isMobile ? 240 : 320,
    },

    xAxis: rangeXAxisOptions,

    series: activeSeries.map((s) => ({
      id: s.id,
      name: `${s.label} (${s.unit})`,
      data: seriesData[s.id] || [],
      color: s.color,
      type: 'spline',
      lineWidth: 2,
      marker: { enabled: false },
    })),
  }), [
    activeSeries,
    seriesData,
    isMobile,
    rangeXAxisOptions,
  ]);

  const toggleSeries = (id) => {
    setVisible((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePeriodClick = (id) => {
    setPeriod(id);
    setCustomStart(null);
    setCustomEnd(null);
  };

  const handleExportCsv = () => {
    if (!rows.length) return;
    const fields = ['timestamp', ...seriesDefs.map((s) => s.field)];
    const header = fields.join(',');
    const lines = rows.map((row) => fields.map((f) => {
      const v = row[f];
      return v === null || v === undefined ? '' : String(v);
    }).join(','));
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `courbes_${device?.id || 'boitier'}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (!device) return null;

  const typeMeta = TYPE_META[device.table] || TYPE_META.climatrack;

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 1300 }}>
      <Box
        sx={{
          position: { xs: 'fixed', md: 'absolute' },
          top: { xs: 0, md: '50%' },
          left: { xs: 0, md: '50%' },
          right: { xs: 0, md: 'auto' },
          bottom: { xs: 0, md: 'auto' },
          transform: { xs: 'none', md: 'translate(-50%, -50%)' },
          width: { xs: '100%', md: 920 },
          height: { xs: '100%', md: 'auto' },
          maxHeight: { xs: '100%', md: '90vh' },
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: C.card,
          borderRadius: { xs: 0, md: '18px' },
          border: { xs: 'none', md: `1px solid ${C.border}` },
          boxShadow: { xs: 'none', md: '0 20px 60px rgba(0,0,0,0.45)' },
          fontFamily: fontStack,
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {/* En-tête */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            px: MOBILE_PAD,
            py: isMobile ? '14px' : '16px',
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: isMobile ? 32 : 34,
                height: isMobile ? 32 : 34,
                borderRadius: '10px',
                flexShrink: 0,
                backgroundColor: 'rgba(34,211,238,0.13)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShowChart sx={{ fontSize: isMobile ? 16 : 18, color: C.accentBlue }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: isMobile ? '15px' : '16px', fontWeight: 700, color: C.textPrimary }}>
                Courbes de données
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: C.textMuted,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {device.name} · #{device.id}
              </Typography>
              {isMobile && (
                <Box sx={{ mt: '6px' }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      borderRadius: '999px',
                      px: '10px',
                      py: '3px',
                      backgroundColor: typeMeta.tint,
                      border: `1px solid ${typeMeta.border}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: typeMeta.color }}>
                      {device.table}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            {!isMobile && (
              <Box
                sx={{
                  display: 'inline-flex',
                  borderRadius: '999px',
                  px: '10px',
                  py: '4px',
                  ml: '4px',
                  flexShrink: 0,
                  alignSelf: 'center',
                  backgroundColor: typeMeta.tint,
                  border: `1px solid ${typeMeta.border}`,
                }}
              >
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: typeMeta.color }}>
                  {device.table}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              cursor: 'pointer',
              backgroundColor: C.input,
              border: `1px solid ${C.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.textMuted,
              flexShrink: 0,
            }}
          >
            <Close sx={{ fontSize: 16 }} />
          </Box>
        </Box>

        {/* Corps scrollable */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            px: MOBILE_PAD,
            py: isMobile ? '12px' : '14px',
            boxSizing: 'border-box',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {/* Barre d'outils */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              mb: '14px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                overflowX: isMobile ? 'auto' : 'visible',
                flexWrap: isMobile ? 'nowrap' : 'wrap',
                alignItems: 'center',
                mx: isMobile ? `calc(-1 * ${MOBILE_PAD})` : 0,
                px: isMobile ? MOBILE_PAD : 0,
                pb: isMobile ? '2px' : 0,
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {PERIODS.map((p) => {
                const on = period === p.id && !customStart;
                return (
                  <Box
                    key={p.id}
                    component="button"
                    type="button"
                    onClick={() => handlePeriodClick(p.id)}
                    sx={{
                      borderRadius: '8px',
                      px: isMobile ? '10px' : '12px',
                      py: '7px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      border: `1px solid ${on ? C.accentBlue : C.border}`,
                      backgroundColor: on ? 'rgba(34,211,238,0.15)' : C.input,
                      color: on ? C.accentBlue : C.textMuted,
                      fontSize: '12px',
                      fontWeight: on ? 700 : 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.label}
                  </Box>
                );
              })}
              <Box
                component="button"
                type="button"
                onClick={(e) => setCalendarAnchor(e.currentTarget)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  borderRadius: '8px',
                  px: isMobile ? '10px' : '12px',
                  py: '7px',
                  cursor: 'pointer',
                  flexShrink: 0,
                  border: `1px solid ${customStart ? C.accentBlue : C.border}`,
                  backgroundColor: customStart ? 'rgba(34,211,238,0.15)' : C.input,
                  color: customStart ? C.accentBlue : C.textMuted,
                  fontSize: '12px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                <CalendarMonthOutlined
                  sx={{
                    fontSize: 14,
                    color: customStart ? '#34D399' : '#34D399',
                  }}
                />
                {isMobile ? 'Choisir des dates précises' : 'Dates précises'}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                borderRadius: '10px',
                p: '4px',
                gap: '4px',
                width: '100%',
                backgroundColor: C.input,
                border: `1px solid ${C.border}`,
                boxSizing: 'border-box',
              }}
            >
              {['combined', 'separate'].map((mode) => {
                const on = viewMode === mode;
                return (
                  <Box
                    key={mode}
                    component="button"
                    type="button"
                    onClick={() => setViewMode(mode)}
                    sx={{
                      flex: 1,
                      borderRadius: '8px',
                      px: '14px',
                      py: '7px',
                      cursor: 'pointer',
                      border: 'none',
                      backgroundColor: on ? C.card : 'transparent',
                      color: on ? C.textPrimary : C.textMuted,
                      fontSize: '12px',
                      fontWeight: on ? 700 : 500,
                    }}
                  >
                    {mode === 'combined' ? 'Combiné' : 'Séparé'}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Popover
            open={Boolean(calendarAnchor)}
            anchorEl={calendarAnchor}
            onClose={() => setCalendarAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={{
              sx: {
                p: 1.5,
                backgroundColor: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: '12px',
              },
            }}
          >
            <Box sx={{ display: 'grid', gap: 1.2, minWidth: 260 }}>
              <TextField
                label="Début"
                type="datetime-local"
                size="small"
                value={toDateTimeLocalValue(customStart)}
                onChange={(event) => {
                  const nextDate = fromDateTimeLocalValue(event.target.value);

                  if (nextDate) {
                    setCustomStart(nextDate);
                    setPeriod('');
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  step: 60,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: C.input,
                    borderRadius: '8px',
                  },

                  '& .MuiInputBase-input': {
                    color: C.textPrimary,
                    fontSize: '13px',
                  },

                  '& .MuiInputLabel-root': {
                    color: C.textMuted,
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: C.border,
                  },

                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(52, 211, 153, 0.55)',
                  },

                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#34D399',
                  },

                  '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                    filter:
                      'invert(69%) sepia(38%) saturate(706%) hue-rotate(104deg) brightness(92%) contrast(88%)',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                }}
              />

              <TextField
                label="Fin"
                type="datetime-local"
                size="small"
                value={toDateTimeLocalValue(customEnd)}
                onChange={(event) => {
                  const nextDate = fromDateTimeLocalValue(event.target.value);

                  if (nextDate) {
                    setCustomEnd(nextDate);
                    setPeriod('');
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  step: 60,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: C.input,
                    borderRadius: '8px',
                  },

                  '& .MuiInputBase-input': {
                    color: C.textPrimary,
                    fontSize: '13px',
                  },

                  '& .MuiInputLabel-root': {
                    color: C.textMuted,
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: C.border,
                  },

                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(52, 211, 153, 0.55)',
                  },

                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#34D399',
                  },

                  '& input[type="datetime-local"]::-webkit-calendar-picker-indicator': {
                    filter:
                      'invert(69%) sepia(38%) saturate(706%) hue-rotate(104deg) brightness(92%) contrast(88%)',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                }}
              />

              <Button
                size="small"
                variant="contained"
                disabled={!customStart || !customEnd || customStart >= customEnd}
                onClick={() => setCalendarAnchor(null)}
                sx={{
                  textTransform: 'none',
                  background: btnGradient,
                  color: '#0B1320',
                  fontWeight: 800,

                  '&:disabled': {
                    opacity: 0.45,
                    color: '#0B1320',
                  },
                }}
              >
                Appliquer
              </Button>
            </Box>
          </Popover>

          {/* Filtres type */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              mb: '14px',
              rowGap: isMobile ? '6px' : '8px',
            }}
          >
            {seriesDefs.map((s) => {
              const on = visible[s.id] !== false;
              const chipLabel = isMobile && s.label === 'Humidité' ? 'Humid.' : s.label;
              return (
                <Box
                  key={s.id}
                  component="button"
                  type="button"
                  onClick={() => toggleSeries(s.id)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    borderRadius: '999px',
                    px: isMobile ? '10px' : '12px',
                    py: '6px',
                    cursor: 'pointer',
                    border: `1px solid ${customStart ? '#34D399' : C.border}`,
                    backgroundColor: customStart ? 'rgba(52,211,153,0.13)' : C.input,
                    color: customStart ? '#34D399' : C.textMuted,
                    fontSize: '12px',
                    fontWeight: on ? 600 : 500,
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.color }} />
                  {chipLabel}
                </Box>
              );
            })}
          </Box>

          {/* Graphiques */}
          <Box
            sx={{
              borderRadius: '12px',
              backgroundColor: C.input,
              p: isMobile ? '12px' : '16px',
              minHeight: isMobile ? 160 : 200,
            }}
          >
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: C.textPrimary, mb: '12px' }}>
              {viewMode === 'combined'
                ? `Vue combinée · ${periodLabel}`
                : `Vue séparée · ${periodLabel}`}
            </Typography>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={32} sx={{ color: C.accentBlue }} />
              </Box>
            )}

            {!loading && error && (
              <Typography sx={{ color: C.textMuted, textAlign: 'center', py: 4 }}>
                {error}
              </Typography>
            )}

            {!loading && !error && activeSeries.length === 0 && (
              <Typography sx={{ color: C.textMuted, textAlign: 'center', py: 4 }}>
                Sélectionnez au moins un type de donnée.
              </Typography>
            )}

            {!loading && !error && activeSeries.length > 0 && rows.length === 0 && (
              <Typography
                sx={{
                  color: C.textMuted,
                  textAlign: 'center',
                  py: 1,
                  fontSize: '12px',
                }}
              >
                Aucune donnée pour cette période.
              </Typography>
            )}

            {!loading && !error && activeSeries.length > 0 && viewMode === 'combined' && (
              <HighchartsReact
                highcharts={Highcharts}
                options={combinedOptions}
              />
            )}

            {!loading && !error && activeSeries.length > 0 && viewMode === 'separate' && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: '12px',
                }}
              >
                {activeSeries.map((s) => {
                  const data = seriesData[s.id] || [];
                  const last = data.length ? data[data.length - 1][1] : null;
                  const trend = getTrendMeta(data, s.id);
                  const trendColor = trend?.color || s.color;
                  const miniHeight = isMobile ? 88 : 120;
                  return (
                    <Box
                      key={s.id}
                      sx={{
                        borderRadius: '12px',
                        backgroundColor: C.card,
                        border: `1px solid ${C.border}`,
                        p: '12px',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '8px', gap: '8px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: C.textPrimary }}>
                            {isMobile && s.label === 'Humidité' ? 'Humid.' : s.label}
                          </Typography>
                          <Typography sx={{ fontSize: '10px', color: C.textMuted }}>{s.unit}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: isMobile ? '16px' : '14px', fontWeight: 700, color: C.textPrimary }}>
                            {last !== null ? last : '—'}
                          </Typography>
                          {isMobile && trend && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', mt: '2px' }}>
                              {trend.up ? (
                                <TrendingUp sx={{ fontSize: 12, color: trendColor }} />
                              ) : (
                                <TrendingDown sx={{ fontSize: 12, color: trendColor }} />
                              )}
                              <Typography sx={{ fontSize: '10px', fontWeight: 600, color: trendColor }}>
                                {trend.label}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={{
                          ...baseChartOptions,

                          chart: {
                            ...baseChartOptions.chart,
                            height: miniHeight,
                          },

                          xAxis: rangeXAxisOptions,

                          series: [{
                            name: s.label,
                            data,
                            color: s.color,
                            type: 'spline',
                            lineWidth: 2,
                            marker: { enabled: false },
                          }],
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>

        {/* Pied de page */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center',
            justifyContent: 'space-between',
            gap: isMobile ? '10px' : '12px',
            px: MOBILE_PAD,
            py: isMobile ? '12px' : '14px',
            pb: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : '14px',
            borderTop: `1px solid ${C.border}`,
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: C.textMuted,
              textAlign: isMobile ? 'center' : 'left',
            }}
          >
            {fetchedAt
              ? `Mis à jour il y a ${Math.max(1, Math.round((Date.now() - fetchedAt.getTime()) / 60000))} min`
              : '—'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={handleExportCsv}
              disabled={!rows.length}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                borderRadius: '10px',
                px: '14px',
                py: '9px',
                flex: isMobile ? 1 : 'none',
                cursor: rows.length ? 'pointer' : 'not-allowed',
                opacity: rows.length ? 1 : 0.5,
                backgroundColor: C.input,
                border: `1px solid ${C.border}`,
                color: C.textMuted,
                fontSize: '12px',
                fontWeight: 600,
              }}
            >
              <Download sx={{ fontSize: 14 }} />
              Exporter CSV
            </Box>
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                borderRadius: '10px',
                px: '18px',
                py: '9px',
                flex: isMobile ? 1 : 'none',
                cursor: 'pointer',
                border: 'none',
                background: btnGradient,
                color: '#0B1320',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              Fermer
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeviceCurvesModal;
